/**
 * AppShellSkeleton — the page's structure while it's still arriving.
 *
 * WHY (T23, 2026-08-23)
 * ---------------------
 * Tapping "Open the demo" bought ~8–13 seconds of a single spinner on an
 * otherwise empty screen. The outreach email earns exactly one tap, and that
 * was what the tap bought. A spinner says "something is happening"; a
 * skeleton says "this is the page you asked for, and here is its shape" —
 * the wait feels shorter because it is legible, and the layout doesn't jump
 * when content lands.
 *
 * The pattern is already in the codebase: MissionCard renders matched-height
 * skeletons for exactly this reason ("keeps My Day visually stable from
 * first paint"). This lifts it to the page level.
 *
 * NOTE ON THE WAIT ITSELF. Most of those seconds are network — dev has no
 * CDN warmth and the audit ran cold. This deliberately doesn't try to make
 * the wait shorter; it changes what fills it. If it's still slow once this
 * lands, measure before optimising (T23).
 */
import { Loader2 } from "lucide-react";

function Block({ className = "" }: { className?: string }) {
  return <div className={`rounded-2xl bg-muted/40 animate-pulse ${className}`} aria-hidden="true" />;
}

export default function AppShellSkeleton({ message }: { message?: string }) {
  return (
    <div
      className="min-h-screen bg-background"
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="app-shell-skeleton"
    >
      {/* Screen-reader text: the visual skeleton means nothing without it,
          and `aria-hidden` on every block would otherwise leave this silent. */}
      <span className="sr-only">{message ?? "Loading"}</span>

      {/* Top chrome — the header band and one row of nav tiles, matching the
          128px simplified chrome so the real header lands where this sat. */}
      <div className="px-4 pt-3" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}>
        <div className="flex items-center gap-3">
          <Block className="w-6 h-6 rounded-lg" />
          <Block className="h-5 w-28 rounded-md" />
          <div className="flex-1" />
          <Block className="w-6 h-6 rounded-full" />
          <Block className="w-6 h-6 rounded-full" />
        </div>
        <div className="mt-4 grid grid-cols-5 gap-1">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <Block className="w-7 h-7 rounded-lg" />
              <Block className="h-2 w-10 rounded" />
            </div>
          ))}
        </div>
      </div>

      {/* The page: a hero, then two cards. Same rhythm as the real My Day. */}
      <div className="px-4 mt-6 max-w-2xl mx-auto w-full space-y-4">
        <div className="rounded-2xl border border-border p-4 space-y-3">
          <Block className="h-3 w-20 rounded" />
          <Block className="h-12 w-32 rounded-lg" />
          <Block className="h-3 w-48 rounded" />
          <Block className="h-11 w-full rounded-xl" />
        </div>
        <Block className="h-24 w-full" />
        <Block className="h-32 w-full" />
      </div>

      {message && (
        <p className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
          {message}
        </p>
      )}
    </div>
  );
}
